/**
 * What the entity system needs from the game, plus the small geometry helpers
 * every entity file shares. DOM-free so node smoke tests can run it all.
 */

import { collisionOf, IS_OPAQUE } from '../blocks';
import type { World } from '../world/chunk';
import type { DamageSource, Stack } from '../game/types';

/**
 * A player the entity system may target: the local one, or - in multiplayer -
 * a remote one the host simulates against (net/game.ts fills
 * `EntityManager.remotePlayers`). Same shape as `EntityHost['player']` so a
 * remote target can stand in for the local one; `id` is '' for the local
 * player and the peer id otherwise, which is how damage finds its owner.
 */
export interface PlayerTarget {
  readonly id: string;
  /** Dedicated server only: the player's entity id (who shot an arrow, who a mob was hit by). */
  readonly eid?: number;
  x: number; y: number; z: number;   // feet
  readonly eyeY: number; readonly width: number; readonly height: number;
  readonly alive: boolean; readonly creative: boolean; readonly spectator: boolean;
  /** Returns 'blocked' when a raised shield took the hit. */
  damage(amount: number, src: DamageSource): void | 'blocked';
  /** (dx, dz) points FROM the attacker TOWARD the player (the push direction). */
  knockback(dx: number, dz: number, strength: number): void;
  setOnFire?(ticks: number): void;
  /** Remote players only: replicated armour item ids, head..feet (piglins check them for gold). */
  readonly armor?: readonly number[];
  /** Item id in this player's hand (what tempts animals). Absent: the host's `heldItem()` (local player). */
  readonly heldItem?: number;
  /** Mob detection-range multiplier for this player (Invisibility). Absent = 1. */
  readonly detectScale?: number;
  /** Ticks since this player last slept or died (phantoms). Absent: the host's `restTicks()`. */
  readonly restTicks?: number;
}

/**
 * Stands in for `EntityHost.player` where there is no local player (the
 * dedicated server): dead, untargetable and nowhere near anything, so AI that
 * reads `host.player` without asking `targetable()` finds nobody.
 */
export const NO_PLAYER: PlayerTarget = {
  id: '', x: 1e9, y: -1e9, z: 1e9, eyeY: -1e9, width: 0.6, height: 1.8,
  alive: false, creative: false, spectator: true,
  damage: () => {}, knockback: () => {},
};

export interface EntityHost {
  /** getBlock, chunk lights via getSky/getBlockLight, chunk `state` (only Ready chunks are simulated). */
  world: World;
  isNight(): boolean;
  /** Ticks of the day, 0..24000 (0 = sunrise, 6000 noon, 12000 sunset, 18000 midnight). Absent: derived from isNight(). */
  dayTime?(): number;
  /** 0..1 daylight at the moment (from sky.time). */
  sunLevel(): number;
  /** Target adapter the lead builds around Player + PlayerStats. */
  player: {
    readonly x: number; readonly y: number; readonly z: number;   // feet
    readonly eyeY: number; readonly width: number; readonly height: number;
    readonly alive: boolean; readonly creative: boolean; readonly spectator: boolean;
    /** Returns 'blocked' when a raised shield took the hit. */
    damage(amount: number, src: DamageSource): void | 'blocked';
    /** (dx, dz) points FROM the attacker TOWARD the player (the push direction). */
    knockback(dx: number, dz: number, strength: number): void;
    setOnFire?(ticks: number): void;
  };
  /** Creeper blast: the lead wires this to TntSim.blast (blocks only; entity damage is done here). */
  explode(x: number, y: number, z: number, power: number): void;
  dropItem(s: Stack, x: number, y: number, z: number, vx?: number, vy?: number, vz?: number): void;
  dropXp(amount: number, x: number, y: number, z: number): void;
  effect(kind: 'hurt' | 'death' | 'explosion_small' | 'poof' | 'heart', x: number, y: number, z: number): void;
  /** Sheep grazing: turn the grass block at (x, y, z) to dirt; false if it is not grass / not editable. */
  eatGrass?(x: number, y: number, z: number): boolean;
  /** World difficulty for Bedrock mob melee scaling (default normal). */
  difficulty?(): 'peaceful' | 'easy' | 'normal' | 'hard';
  /** Farmland under (x, y, z) was trampled (world/crops.ts trampleCheck): turn it to dirt. */
  trample?(x: number, y: number, z: number): void;
  /** Mob-driven block edit (a villager opening a door, a zombie breaking one). Absent: doors stay as they are. */
  setBlock?(x: number, y: number, z: number, id: number): void;
  /** Rain falling at a cell (weather.isRainingAt); absent = never raining. */
  isRainingAt?(x: number, y: number, z: number): boolean;
  /** Mob detection-range multiplier for the local player (Invisibility: 0.07..0.7, see effects.visibilityScale); absent = 1. */
  detectScale?(): number;
  /** Item id in the local player's hand (0 = empty): what tempts animals. */
  heldItem?(): number;
  /** Door at (x, y, z) being broken: crack stage 0..9, -1 = progress reset. */
  crackDoor?(x: number, y: number, z: number, stage: number): void;
  /** Ticks since the player last slept or died (phantom spawning); absent = never. */
  restTicks?(): number;
  /** Seeded / Math.random, 0..1. */
  rnd(): number;
}

/** Yaw (player convention, forward = (-sin, 0, -cos)) that faces the direction (dx, dz). */
export const yawTo = (dx: number, dz: number): number => Math.atan2(-dx, -dz);

/** Move angle `a` toward `b` by at most `max` radians, taking the short way round. */
export function approachAngle(a: number, b: number, max: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  else if (d < -Math.PI) d += Math.PI * 2;
  return Math.abs(d) <= max ? a + d : a + Math.sign(d) * max;
}

/** Wrap an angle to -PI..PI. */
export function wrapAngle(a: number): number {
  a %= Math.PI * 2;
  if (a > Math.PI) a -= Math.PI * 2;
  else if (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * Light the mobs care about at a cell, 0..15: the brighter of block light and
 * sky light scaled by the current daylight (so a lit-by-moon night is dark).
 */
export function effectiveLight(host: EntityHost, x: number, y: number, z: number): number {
  const w = host.world;
  const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
  const sky = Math.floor(w.getSky(bx, by, bz) * host.sunLevel());
  const bl = w.getBlockLight(bx, by, bz);
  return sky > bl ? sky : bl;
}

/**
 * Is the straight segment free of anything solid? Vanilla clips sight and blast
 * rays against collision shapes (ClipContext.Block.COLLIDER), so glass, closed
 * doors, leaves and fences hide the player just like stone does; only cells
 * with no collision (air, water, plants, an open doorway) let the ray through.
 * Walks the cells the segment crosses and tests their boxes exactly.
 */
export function lineClear(world: World, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean {
  const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
  let cx = Math.floor(x0), cy = Math.floor(y0), cz = Math.floor(z0);
  const ex = Math.floor(x1), ey = Math.floor(y1), ez = Math.floor(z1);
  const sx = dx > 0 ? 1 : -1, sy = dy > 0 ? 1 : -1, sz = dz > 0 ? 1 : -1;
  const tdx = dx !== 0 ? Math.abs(1 / dx) : Infinity, tdy = dy !== 0 ? Math.abs(1 / dy) : Infinity, tdz = dz !== 0 ? Math.abs(1 / dz) : Infinity;
  let tx = dx !== 0 ? (dx > 0 ? cx + 1 - x0 : x0 - cx) * tdx : Infinity;
  let ty = dy !== 0 ? (dy > 0 ? cy + 1 - y0 : y0 - cy) * tdy : Infinity;
  let tz = dz !== 0 ? (dz > 0 ? cz + 1 - z0 : z0 - cz) * tdz : Infinity;
  const box = [0, 0, 0, 0, 0, 0];
  for (let guard = 0; guard < 512; guard++) {
    const id = world.getBlock(cx, cy, cz);
    if (id !== 0) {
      if (IS_OPAQUE[id] === 1) return false;
      const b = collisionOf(id);
      for (let k = 0; k < b.length; k += 6) {
        box[0] = cx + b[k] / 16; box[1] = cy + b[k + 1] / 16; box[2] = cz + b[k + 2] / 16;
        box[3] = cx + b[k + 3] / 16; box[4] = cy + b[k + 4] / 16; box[5] = cz + b[k + 5] / 16;
        if (segmentAabb(x0, y0, z0, dx, dy, dz, box) >= 0) return false;
      }
    }
    if (cx === ex && cy === ey && cz === ez) return true;
    if (tx <= ty && tx <= tz) { if (tx > 1) return true; cx += sx; tx += tdx; }
    else if (ty <= tz) { if (ty > 1) return true; cy += sy; ty += tdy; }
    else { if (tz > 1) return true; cz += sz; tz += tdz; }
  }
  return true;
}

/**
 * Slab test of the segment o + t*d (t in 0..1) against an AABB
 * [x0,y0,z0,x1,y1,z1]. Returns the entry t (0 when the origin is inside) or -1.
 */
export function segmentAabb(
  ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
  b: readonly number[],
): number {
  let t0 = 0, t1 = 1;
  const o = [ox, oy, oz], d = [dx, dy, dz];
  for (let a = 0; a < 3; a++) {
    if (Math.abs(d[a]) < 1e-12) {
      if (o[a] < b[a] || o[a] > b[a + 3]) return -1;
    } else {
      let ta = (b[a] - o[a]) / d[a];
      let tb = (b[a + 3] - o[a]) / d[a];
      if (ta > tb) { const s = ta; ta = tb; tb = s; }
      if (ta > t0) t0 = ta;
      if (tb < t1) t1 = tb;
      if (t0 > t1) return -1;
    }
  }
  return t0;
}
